import React, { useEffect, useState } from 'react';
import { Button, Group, Paper, Stack, Textarea, Title } from '@mantine/core';
import { inject, observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';
import { notifications } from '@mantine/notifications';
import { useGetMedicalNote } from '../../../global/entities/MedicalNote/queries/useGetMedicalNote';
import { useUpdateMedicalNoteMutation } from '../../../global/entities/MedicalNote/mutations/useUpdateMedicalNoteMutation';
import { PatientStore } from '../../../global/Store/PatientStore.ts';
import HcpOnlyCaption from '../../../global/component/molecules/HcpOnlyCaption/HcpOnlyCaption.tsx';

interface INotesProps {
  patientStore?: PatientStore;
}

const Notes: React.FC<INotesProps> = inject('patientStore')(
  observer(({ patientStore }) => {
    const { t } = useTranslation();

    // State
    const [note, setNote] = useState('');

    // Queries and Mutations
    const { data: medicalNote, isLoading: isLoadingNote } = useGetMedicalNote(
      patientStore?.patientId,
    );
    const { mutate: updateNote } = useUpdateMedicalNoteMutation(
      patientStore?.patientId,
    );

    // Effects
    useEffect(() => {
      if (medicalNote) setNote(medicalNote.note || '');
    }, [medicalNote]);

    // Handlers
    const handleSave = async () => {
      updateNote(
        { note },
        {
          onSuccess: () => {
            notifications.show({
              title: t('button.save'),
              message: t('notes.saveSuccess'),
              color: 'green',
              withCloseButton: true,
              autoClose: 3000,
            });
          },
        },
      );
    };

    const handleCancel = () => {
      if (medicalNote) setNote(medicalNote.note || '');
    };

    // Render
    return (
      <Paper h={'100%'} style={{ display: 'flex', flexDirection: 'column' }}>
        <Stack h={'100%'}>
          <Stack gap={2}>
            <Title order={4}>{t('notes.title')}</Title>
            <HcpOnlyCaption />
          </Stack>
          <Textarea
            h={'100%'}
            placeholder={t('notes.addNotesPlaceholder')}
            minRows={4}
            style={{ flexGrow: 1, minHeight: '200px' }}
            styles={{
              wrapper: { height: '100%', minHeight: '200px' },
              input: { height: '100%', minHeight: '200px' },
            }}
            value={note}
            onChange={(e) => setNote(e.currentTarget.value)}
            disabled={isLoadingNote}
          />
          <Group mt="auto" w={'100%'}>
            <Button
              style={{ flex: 1, marginRight: '5px' }}
              variant="outline"
              onClick={handleCancel}
              disabled={isLoadingNote}
            >
              {t('button.cancel')}
            </Button>
            <Button
              style={{ flex: 1, marginLeft: '5px' }}
              onClick={handleSave}
              loading={isLoadingNote}
            >
              {t('button.save')}
            </Button>
          </Group>
        </Stack>
      </Paper>
    );
  }),
);

export default Notes;
